import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api } from "@/lib/api";
import { Building2, ArrowRight, Users, CheckCircle2, PauseCircle, AlertTriangle } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [societies, setSocieties] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, list] = await Promise.all([
          api.get("/admin/dashboard-stats"),
          api.get("/admin/societies"),
        ]);
        setStats(s.data);
        setSocieties(list.data);
      } catch {/* ignore */}
    })();
  }, []);

  return (
    <AdminLayout
      subtitle="Portal Overview"
      title="All societies at a glance"
      actions={
        <Link to="/admin/societies/new" data-testid="new-society-cta"
          className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-5 py-2.5 text-sm font-medium">
          <Building2 size={16}/> New society <ArrowRight size={14}/>
        </Link>
      }
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={Building2} label="Societies" value={stats?.societies ?? "—"} testid="stat-societies" />
        <Stat icon={CheckCircle2} label="Active" value={stats?.active ?? "—"} testid="stat-active" tint="text-emerald-700" />
        <Stat icon={PauseCircle} label="Suspended" value={stats?.suspended ?? "—"} testid="stat-suspended" tint="text-amber-700" />
        <Stat icon={AlertTriangle} label="Renewals ≤30d" value={stats?.renewals_soon ?? "—"} testid="stat-renewals" tint="text-terracotta"/>
      </div>

      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Recent societies</div>
          <Link to="/admin/societies" className="text-sm text-terracotta hover:text-terracotta-hover">View all →</Link>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          {societies.length === 0 ? (
            <div className="p-10 text-center text-stone-500">
              No societies yet. <Link to="/admin/societies/new" className="text-terracotta">Onboard your first one →</Link>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-sand-50 border-b border-stone-200">
                <tr>
                  <Th>Society</Th><Th>Slug</Th><Th>Units</Th><Th>Status</Th><Th>Renewal</Th><Th></Th>
                </tr>
              </thead>
              <tbody>
                {societies.slice(0, 8).map((s) => (
                  <tr key={s.id} className="border-b border-stone-100 hover:bg-sand-50/60 transition-colors" data-testid={`society-row-${s.slug}`}>
                    <Td><div className="font-medium">{s.name}</div><div className="text-xs text-stone-500">{s.registration_number || "—"}</div></Td>
                    <Td className="text-stone-600">{s.slug}</Td>
                    <Td>{s.unit_count_at_signup ?? 0}</Td>
                    <Td><StatusBadge status={s.subscription_status} /></Td>
                    <Td className="text-stone-600">{s.subscription_end_date}</Td>
                    <Td><Link to={`/admin/societies/${s.id}`} className="text-terracotta hover:text-terracotta-hover text-sm">Open →</Link></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Stat({ icon: Icon, label, value, testid, tint = "text-ink" }) {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between text-stone-500 text-xs uppercase tracking-widest font-semibold">
        {label}<Icon size={16} strokeWidth={1.5} className={tint}/>
      </div>
      <div className="editorial text-4xl mt-4">{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active: "bg-emerald-100 text-emerald-800",
    Suspended: "bg-amber-100 text-amber-800",
    Expired: "bg-red-100 text-red-800",
  };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${map[status] || "bg-stone-100 text-stone-800"}`}>{status || "—"}</span>;
}

function Th({ children }) { return <th className="text-xs uppercase tracking-wider font-semibold text-stone-500 py-4 px-5">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`py-4 px-5 text-sm ${className}`}>{children}</td>; }
